import { useEffect, useState } from 'react'
import Pagination from '../components/commons/Pagination'
import { getAllCategories, setCategoryState } from '../firebase/categories'
import { toast } from 'react-toastify'
import { formatDate } from '../utils'
import DialogCreateCategory from '../components/dialogs/DialogCreateCategory'
import { useAtom } from 'jotai'
import { showCreateCategoryModalAtom, showManageCategoryModalAtom } from '../store'
import DialogManageCategory from '../components/dialogs/DialogManageCategory'
import { getListingsByCategories, getSalesByCategories } from '../firebase/listings'

const CategoryItem = ({ cat, handleManageCategory, onStateChange }) => {
    const [listingsCount, setListingsCount] = useState(0)
    const [salesCount, setSalesCount] = useState(0)
    const [savingState, setSavingState] = useState(false)

    const isActive = cat.state === 'active'

    // Shop-facing reads ask for state === 'active' only, so this is the one
    // switch that decides whether shoppers ever see the category.
    const handleToggleState = async () => {
        const next = isActive ? 'pending' : 'active'
        setSavingState(true)
        const ok = await setCategoryState(cat.id, next)
        setSavingState(false)

        if (!ok) {
            toast.error(`Could not update ${cat.name}.`)
            return
        }

        onStateChange(cat.id, next)
        toast.success(next === 'active'
            ? `${cat.name} is live on the platform.`
            : `${cat.name} is hidden from the platform.`)
    }

    useEffect(() => {
        getListingsByCategories([cat.id]).then((data) => {
            setListingsCount(data.length)
        })
        getSalesByCategories([cat.id]).then((data) => {
            setSalesCount(data.length)
        })
    }, [])

    return (
        <tr key={cat.id} className='h-[70px] hover:bg-[#2F2F2F] p-2'>
            <td className='w-[100px] pl-2'>
                <img alt='picture' src={cat.image} className='w-[55px] h-[55px] object-cover' />
            </td>

            <td>
                {cat.name}
            </td>

            <td>
                {cat.created_at ? formatDate(cat.created_at.seconds) : 'N/A'}
            </td>

            <td>
                {listingsCount}
            </td>

            <td>
                {salesCount}
            </td>

            <td>
                <button
                    className={`px-4 py-1 rounded-[33px] text-xs whitespace-nowrap ${isActive ? 'bg-primary text-white' : 'bg-[#393939] text-[#959595]'} ${savingState ? 'opacity-50' : ''}`}
                    onClick={handleToggleState}
                    disabled={savingState}
                    title={isActive
                        ? 'Live on the platform. Click to hide it.'
                        : 'Hidden from the platform. Click to publish it.'}
                >
                    {savingState ? 'Saving...' : (isActive ? 'Active' : 'Pending')}
                </button>
            </td>

            <td>
                <div className='flex justify-end pr-10'>
                    <button
                        className="px-6 py-2 bg-white text-black rounded-[33px]"
                        onClick={() => handleManageCategory(cat)}
                    >Manage Category</button>
                </div>
            </td>
        </tr>
    )
}

// 'pending' is anything not active: the page only loads active and pending,
// and the Status toggle reads active the same way, so a category with a
// missing or unexpected state is shown as pending rather than vanishing from
// every filter.
const STATUS_FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' }
]

const matchesStatus = (cat, filter) => {
    if (filter === 'all') return true
    if (filter === 'active') return cat.state === 'active'
    return cat.state !== 'active'
}

const CategoryManagement = () => {
    const [currentPage, setCurrentPage] = useState(1)
    const [statusFilter, setStatusFilter] = useState('all')
    const [categories, setCategories] = useState(null)
    const [loading, setLoading] = useState(true)
    const [updatedTimes, setUpdatedTimes] = useState(0)
    const [showCreateCategoryModal, setShowCreateCategoryModal] = useAtom(showCreateCategoryModalAtom)
    const [showManageCategoryModal, setShowManageCategoryModal] = useAtom(showManageCategoryModalAtom)
    const [selectedCategory, setSelectedCategory] = useState(null)
    const fields = ["", "Category name", 'Date added', 'Category listings', 'Category sales', 'Status', '']

    useEffect(() => {
        updateCategories()
    }, [showCreateCategoryModal, showManageCategoryModal])

    const updateCategories = () => {
        getAllCategories(["active", "pending"]).then((cats) => {
            setCategories(cats)
            setLoading(false)
        })

        setUpdatedTimes(updatedTimes + 1)
    }

    // Patched in place rather than refetched: the whole table is swapped for
    // a loading panel on a reload, which is a lot of movement for one
    // category's status.
    const applyState = (id, state) => {
        setCategories((prev) => prev.map((cat) => (cat.id === id ? { ...cat, state } : cat)))
    }

    const handleManageCategory = (cat) => {
        setSelectedCategory(cat)
        setShowManageCategoryModal(true)
    }

    const changeStatusFilter = (value) => {
        setStatusFilter(value)
        // Page 3 of every category is not page 3 of the pending ones.
        setCurrentPage(1)
    }

    const allCategories = categories || []
    const visibleCategories = allCategories.filter((cat) => matchesStatus(cat, statusFilter))
    const countFor = (filter) => allCategories.filter((cat) => matchesStatus(cat, filter)).length

    return (
        <>
            <DialogCreateCategory
                open={showCreateCategoryModal}
                onClose={() => setShowCreateCategoryModal(false)}
            />

            {
                selectedCategory &&
                <DialogManageCategory
                    open={showManageCategoryModal}
                    category={selectedCategory}
                    onClose={() => setShowManageCategoryModal(false)}
                />
            }

            <div className="px-10 py-8">
                <div className="flex items-center justify-between">
                    <h1 className="text-primary text-[36px]">Category Manager</h1>

                    <button className="px-6 py-2 bg-white rounded-[33px]" onClick={() => setShowCreateCategoryModal(true)}>Add New</button>
                </div>

                {!loading && (
                    <div className="flex items-center gap-2 mt-8">
                        {STATUS_FILTERS.map((filter) => {
                            const selected = statusFilter === filter.value

                            return (
                                <button
                                    key={filter.value}
                                    className={`px-5 py-2 rounded-[33px] text-sm transition-colors ${selected
                                        ? 'bg-primary text-white'
                                        : 'bg-[#2E2E2E] text-[#959595] hover:text-white'}`}
                                    onClick={() => changeStatusFilter(filter.value)}
                                    aria-pressed={selected}
                                >
                                    {filter.label}
                                    <span className={`ml-2 text-xs ${selected ? 'text-white/70' : 'text-[#717171]'}`}>
                                        {countFor(filter.value)}
                                    </span>
                                </button>
                            )
                        })}
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="flex flex-col items-center">
                            <img src="/assets/icons/icon-loading.svg" alt="loading" className="w-8 h-8 animate-spin mb-4" />
                            <p className="text-white text-lg">Loading categories...</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="my-10">
                            {visibleCategories.length === 0 ? (
                                <p className="text-white text-center py-16">
                                    {statusFilter === 'all'
                                        ? 'No categories yet'
                                        : `No ${statusFilter} categories`}
                                </p>
                            ) : (
                            <div className="w-full overflow-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-[#2E2E2E] rounded-[4px] py-3 text-sm text-[#717171]">
                                            {
                                                fields.map((item, index) =>
                                                    <th className={`py-3 text-left ${index > 0 ? '' : 'pl-3'}`} key={`offer-field-${item}-${index}`}>
                                                        {item}
                                                    </th>
                                                )
                                            }
                                        </tr>
                                    </thead>

                                    <tbody className="text-white space-y-1 text-sm">
                                        {
                                            visibleCategories.slice(8 * (currentPage - 1), 8 * currentPage).map(cat =>
                                                <CategoryItem
                                                    key={cat.id}
                                                    cat={cat}
                                                    handleManageCategory={handleManageCategory}
                                                    onStateChange={applyState}
                                                />
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                            )}
                        </div>

                        <div className="mt-auto">
                            {/* Keyed on the filter so it remounts: Pagination
                                keeps its page in its own state and does not
                                reset when totalItems shrinks, which otherwise
                                left the table on an out-of-range page after
                                switching filters. */}
                            <Pagination
                                key={statusFilter}
                                itemsPerPage={8}
                                totalItems={visibleCategories.length}
                                onPageChange={(v) => { setCurrentPage(v) }}
                            />
                        </div>
                    </>
                )}
            </div>
        </>
    )
}

export default CategoryManagement